import React, { useState } from "react"
import PropTypes from "prop-types"
import CarrotPropTypes from "utils/CarrotPropTypes"
import L10n from "lib/carrot-l10n"
import DefaultMarkdown from "components/views/DefaultMarkdown"
import WizardWrapper from "components/views/atoms/forms/WizardWrapper"
import { useDidMount, useToggle } from "utils/Hooks"
import { withRouter } from "react-router-dom"
import { HelperText, Spacer } from "../atoms/Atoms"
import FormMultiSelect from "components/views/atoms/forms/FormMultiSelect"
import { MultiPathSVG } from "../SVG"
import { BodySmall } from "../atoms/Bodies"
import { WhyWereAskingModal } from "./WhyWereAskingModal"
import { WhyAreWeAskingModule } from "./WhyWereAskingModule"
import { PageHeader } from "../molecules/Molecules"
import { MasterLayout } from "../atoms/layouts/MasterLayout"
import { BodyWithRightSidebarLayout } from "../atoms/layouts/BodyWithRightSidebarLayout"
import { H3 } from "@carrotfertility/carotene"
import { LongHeaderAndInput } from "components/views/atoms/forms/FormLongHeader"
import { FormRadioGroup } from "components/views/atoms/forms/FormRadioGroup"
import { FormTextArea } from "components/views/atoms/forms/FormTextArea"
import { SideBarBase } from "components/views/atoms/SidebarBase"
import { Large } from "components/views/atoms/Responsive"
import { getHasDeductible } from "derivedSelectors/deductibleSelectors"
import { useSelector } from "react-redux"
import { getHasCostShare } from "derivedSelectors"
import { StringParam, useQueryParam } from "use-query-params"
import { CarrotQueryParam, CarrotRoute, getReimbursementUrl } from "utils/CarrotUrls"
import { HowMuchReimbursedModule } from "components/views/reimbursements/educationmodal/HowMuchReimbursedModule"
import { whoReceivedCareChoices } from "components/views/reimbursements/whoReceivedCareChoices"
import { getBenefitConfiguration } from "reducers/benefitConfiguration"
import { FeatureGate } from "utils/FeatureGate"
import { ConnectInsuranceInfoModule } from "./ConnectInsuranceInfoModule"
import { BenefitEnrollmentFlowContainer } from "components/csd-enrollment"
import { useConnectInsurance } from "../../context/enrollment/ConnectInsuranceContext"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const isYesNoChoices = [
  { label: l`client.confirm.yes`, id: "true" },
  { label: l`client.confirm.no`, id: "false" }
]

const Submit = ({
  onDone,
  sending,
  expenseId,
  fields,
  handleInputChange,
  formNotComplete,
  showErrorMessage,
  attachments,
  history
}) => {
  const [reimbursementType] = useQueryParam(CarrotQueryParam.ReimbursementType, StringParam)

  useDidMount(() => {
    if (attachments.length === 0) {
      history.push(getReimbursementUrl(CarrotRoute.Upload, expenseId, reimbursementType))
    }
  })

  return (
    <MasterLayout header={<SubmitHeader />}>
      <BodyWithRightSidebarLayout sidebar={<SubmitSidebar />}>
        <SubmitBody
          onDone={onDone}
          sending={sending}
          expenseId={expenseId}
          fields={fields}
          handleInputChange={handleInputChange}
          formNotComplete={formNotComplete}
          showErrorMessage={showErrorMessage}
          reimbursementType={reimbursementType}
        />
      </BodyWithRightSidebarLayout>
    </MasterLayout>
  )
}

const SubmitHeader = () => <PageHeader title={l`client.reimbursements.navText`} icon="icn-get-reimbursed" />

const SubmitSidebar = () => {
  const hasDeductible = useSelector(getHasDeductible)
  const hasCostShare = useSelector(getHasCostShare)
  const { showOptionalInsuranceInfoPromo } = useConnectInsurance()

  const [showBenefitEnrollmentModal, toggleBenefitEnrollmentModal] = useToggle(false)

  return (
    <Large>
      <SideBarBase>
        <FeatureGate filter={(c) => c.isLgtbqEnabled}>
          <WhyAreWeAskingModule />
        </FeatureGate>
        {hasDeductible || hasCostShare ? <HowMuchReimbursedModule /> : null}
        {showOptionalInsuranceInfoPromo && <ConnectInsuranceInfoModule onClickEnroll={toggleBenefitEnrollmentModal} />}
      </SideBarBase>
      {showBenefitEnrollmentModal && <BenefitEnrollmentFlowContainer onExit={toggleBenefitEnrollmentModal} />}
    </Large>
  )
}

const SubmitBody = ({ onDone, sending, expenseId, fields, handleInputChange, showErrorMessage, reimbursementType }) => {
  const [showWhyWereAskingModal, toggleWhyWereAskingModal] = useToggle(false)
  const [accordionState, setAccordionState] = useState({
    isInfertility: false,
    isMedicalNecessity: false
  })
  const { featureConfig } = useSelector(getBenefitConfiguration)

  const filteredWhoReceivedCareChoices = whoReceivedCareChoices.filter((c) => !c.filter || c.filter(featureConfig))

  const openHelpModal = ({ isInfertility = false, isMedicalNecessity = false }) => {
    setAccordionState({
      isInfertility: isInfertility,
      isMedicalNecessity: isMedicalNecessity
    })
    toggleWhyWereAskingModal()
  }

  const handleSubmit = () => {
    onDone(expenseId)
  }

  const errors = {
    whoReceivedCare: {
      message: "Select at least one option"
    },
    isInfertility: {
      message: "Select Yes or No"
    },
    isMedicalNecessity: {
      message: "Select Yes or No"
    }
  }

  const ValidationMessage = ({ message, valid }) => {
    return (
      <div className="mt1 ml3" style={{ height: "1.5rem" }}>
        {valid ? (
          ""
        ) : (
          <>
            <MultiPathSVG iconName="X" className="dib v-mid" />
            <div className="dib ml2">
              <HelperText color="invalid-red">{message}</HelperText>
            </div>
          </>
        )}
      </div>
    )
  }

  const ErrorMessageDisplay = ({ field }) => {
    return <ValidationMessage message={errors[field].message} valid={fields[field].valid} />
  }

  const headers = {
    IVF: {
      isInfertilityDiagnosis: l`Was the care received related to an infertility diagnosis issued by your doctor?`,
      isMedicallyNecessary: l`Was the care received considered medically necessary by your doctor due to another treatment or 
              procedure that could impact reproductive health such as chemotherapy, radiation, or surgery?`
    },
    nonIVF: {
      isInfertilityDiagnosis: l`If fertility care was received, was the care related to an infertility diagnosis issued by your doctor?`,
      isMedicallyNecessary: l`If preservation care was received, was the care considered medically necessary by your doctor due to 
              another treatment or procedure that could impact reproductive health such as chemotherapy, radiation, or surgery?`
    }
  }

  return (
    <>
      <WizardWrapper
        back
        backRoute="/reimbursements/upload"
        done
        nextId="submit-reimbursement-button"
        onDone={handleSubmit}
        doneText={l`submit`}
        sending={sending}
        sendingText={l`client.submitting`}
      >
        <LongHeaderAndInput header={l`Who received the care or services provided?`} />
        <FormMultiSelect
          name="whoReceivedCare"
          choices={filteredWhoReceivedCareChoices}
          handleInputChange={handleInputChange}
          valid={fields.whoReceivedCare.valid}
        />
        <ErrorMessageDisplay field={"whoReceivedCare"} />
        <Spacer height={0.5} />
        <LongHeaderAndInput
          header={
            reimbursementType === "IVF" ? headers.IVF.isInfertilityDiagnosis : headers.nonIVF.isInfertilityDiagnosis
          }
          explanationOnClick={featureConfig.isLgtbqEnabled ? () => openHelpModal({ isInfertility: true }) : null}
          testId={"reimbursements-tooltip-infertility"}
        />
        <Spacer height={0.5} />
        <FormRadioGroup
          readOnly={sending}
          error={!fields.isInfertility.valid}
          name="isInfertility"
          choices={isYesNoChoices}
          currentValue={fields.isInfertility.value}
          handleInputChange={handleInputChange}
        />
        <ErrorMessageDisplay field={"isInfertility"} />
        <Spacer height={0.5} />
        <LongHeaderAndInput
          header={reimbursementType === "IVF" ? headers.IVF.isMedicallyNecessary : headers.nonIVF.isMedicallyNecessary}
          explanationOnClick={featureConfig.isLgtbqEnabled ? () => openHelpModal({ isMedicalNecessity: true }) : null}
          testId={"reimbursements-tooltip-medical-necessity"}
        />
        <Spacer height={0.5} />
        <FormRadioGroup
          readOnly={sending}
          error={!fields.isMedicalNecessity.valid}
          name="isMedicalNecessity"
          choices={isYesNoChoices}
          currentValue={fields.isMedicalNecessity.value}
          handleInputChange={handleInputChange}
        />
        <ErrorMessageDisplay field={"isMedicalNecessity"} />
        <Spacer height={0.5} />
        <LongHeaderAndInput header={l`client.reimbursements.anythingElse`} />
        <FormTextArea
          value={fields.memberNotes.value}
          onChange={handleInputChange}
          name="memberNotes"
          maxLength="2048"
        />
        <Spacer height={1.5} />
        <BodySmall color={"black-60"}>
          By submitting, you’re confirming that the information you’ve provided is complete, truthful, and accurate.
        </BodySmall>
        {showErrorMessage && (
          <>
            <Spacer height={0.5} />
            <H3 variant="dark" stack="tiny">
              <DefaultMarkdown source={l`client.error.submitReimbursement`} />
            </H3>
          </>
        )}
        {showWhyWereAskingModal && (
          <WhyWereAskingModal
            expandIsInfertility={accordionState.isInfertility}
            expandIsMedicalNecessity={accordionState.isMedicalNecessity}
            onExit={openHelpModal}
          />
        )}
      </WizardWrapper>
    </>
  )
}

Submit.propTypes = {
  formNotComplete: PropTypes.bool.isRequired,
  showErrorMessage: PropTypes.bool.isRequired,
  fields: PropTypes.shape({
    isInfertility: CarrotPropTypes.field,
    whoReceivedCare: PropTypes.shape({
      value: PropTypes.array
    }),
    isMedicalNecessity: CarrotPropTypes.field,
    memberNotes: CarrotPropTypes.field
  }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  sending: PropTypes.bool,
  history: PropTypes.object.isRequired,
  attachments: PropTypes.array.isRequired
}

export default withRouter(Submit)
